import {
  WizardContentHeader,
  WizardContentHeaderDescription,
  WizardContentHeaderKicker,
  WizardContentHeaderTitle,
  WizardPageLayout,
  WizardPageMainColumn,
  WizardPageStickyColumn,
} from '@/components/Wizard'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import {
  AutoSkeleton,
  IsSkeletonContext,
  Skeleton,
  SkeletonList,
  useSkeletonListContext,
} from '@/components/Skeleton'
import {
  WizardNav,
  WizardNavList,
  WizardNavListItem,
  WizardNavScroller,
} from '@/components/Wizard/WizardNav'
import {
  CheckoutView,
  CheckoutViewBillingMethodsSection,
  CheckoutViewCouponCodeInputField,
  CheckoutViewCouponSection,
  CheckoutViewFooter,
  CheckoutViewMain,
  CheckoutViewMainContent,
  CheckoutViewMainHeader,
  CheckoutViewMainHeaderBulletList,
  CheckoutViewMainHeaderBulletListItem,
  CheckoutViewMainHeaderContent,
  CheckoutViewMainHeaderTitle,
  CheckoutViewSummarySkeleton,
} from '@/features/jobs/drafting/checkout/components/CheckoutView'
import {
  BillingMethodList,
  BillingMethodListItemSkeleton,
} from '@/features/billing/profile/BillingMethodList'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'

interface DraftJobWizardDefaultSkeletonProps {
  testId?: string
}

/**
 * Skeleton view for the draft creation page.
 *
 * Use inside a {@link Wizard} component.
 */
export function DraftJobWizardDefaultSkeleton({
  testId,
}: DraftJobWizardDefaultSkeletonProps) {
  const isSpacious = useIsSpacious()
  return (
    <WizardPageLayout testId={testId}>
      {isSpacious && <DraftJobWizardDefaultStickyColumnNavSkeleton />}
      <WizardPageMainColumn>
        <DraftJobWizardDefaultWizardContentSkeleton />
      </WizardPageMainColumn>
    </WizardPageLayout>
  )
}

/**
 * Skeleton for the sticky nav.
 */
export function DraftJobWizardDefaultStickyColumnNavSkeleton() {
  return (
    <IsSkeletonContext value={true}>
      <WizardPageStickyColumn stretch>
        <WizardNav>
          <WizardNavScroller>
            <WizardNavList>
              <SkeletonList count={8}>
                <SkeletonNavListItem />
              </SkeletonList>
            </WizardNavList>
          </WizardNavScroller>
        </WizardNav>
      </WizardPageStickyColumn>
    </IsSkeletonContext>
  )
}

/**
 * Skeleton for the main column.
 */
export function DraftJobWizardDefaultWizardContentSkeleton() {
  return (
    <IsSkeletonContext value={true}>
      <WizardContentHeader>
        <WizardContentHeaderKicker>
          <AutoSkeleton characters={8} />
        </WizardContentHeaderKicker>
        <WizardContentHeaderTitle>
          <AutoSkeleton characters={40} />
        </WizardContentHeaderTitle>
        <WizardContentHeaderDescription>
          <Skeleton width="90%" />
          <br />
          <Skeleton width="95%" />
          <br />
          <Skeleton width="40%" />
        </WizardContentHeaderDescription>
      </WizardContentHeader>
    </IsSkeletonContext>
  )
}

/**
 * Skeleton Nav List Item
 */
function SkeletonNavListItem() {
  const { vary } = useSkeletonListContext()
  // Take up a varying amount of space.
  return <WizardNavListItem label={'\u00A0'.repeat(vary(25))} />
}

interface DraftJobWizardCheckoutSkeletonProps {
  isBillingEnabled?: boolean
  isSummaryVisible?: boolean
}

/**
 * Draft Job Wizard Checkout Skeleton
 */
export function DraftJobWizardCheckoutSkeleton({
  isBillingEnabled,
  isSummaryVisible,
}: DraftJobWizardCheckoutSkeletonProps) {
  return (
    <IsSkeletonContext value={true}>
      <CheckoutView
        summary={isSummaryVisible && <CheckoutViewSummarySkeleton />}
        footer={
          <>
            {isBillingEnabled && (
              <CheckoutViewCouponSection>
                <CheckoutViewCouponCodeInputField isSkeleton />
              </CheckoutViewCouponSection>
            )}
            <CheckoutViewFooter>
              <ButtonGroup stretch stacked>
                <Button label="Continue" variant="default" isSkeleton />
              </ButtonGroup>
            </CheckoutViewFooter>
          </>
        }
      >
        <CheckoutViewMain>
          <CheckoutViewMainHeader>
            <CheckoutViewMainHeaderTitle>
              <AutoSkeleton characters={20} />
            </CheckoutViewMainHeaderTitle>
            <CheckoutViewMainHeaderContent>
              <CheckoutViewMainHeaderBulletList>
                <CheckoutViewMainHeaderBulletListItem>
                  <AutoSkeleton characters={35} />
                </CheckoutViewMainHeaderBulletListItem>
                <CheckoutViewMainHeaderBulletListItem>
                  <AutoSkeleton characters={40} />
                </CheckoutViewMainHeaderBulletListItem>
                <CheckoutViewMainHeaderBulletListItem>
                  <AutoSkeleton characters={30} />
                </CheckoutViewMainHeaderBulletListItem>
              </CheckoutViewMainHeaderBulletList>
            </CheckoutViewMainHeaderContent>
          </CheckoutViewMainHeader>
          {isBillingEnabled && (
            <CheckoutViewMainContent>
              <CheckoutViewBillingMethodsSection isSkeleton>
                <BillingMethodList aria-label="Placeholder list">
                  <BillingMethodListItemSkeleton />
                  <BillingMethodListItemSkeleton />
                  <BillingMethodListItemSkeleton />
                </BillingMethodList>
              </CheckoutViewBillingMethodsSection>
            </CheckoutViewMainContent>
          )}
        </CheckoutViewMain>
      </CheckoutView>
    </IsSkeletonContext>
  )
}
